import { useRef, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Card } from '@structural-climbing/ui'

interface Props {
  /** One chart per slide, each with its own title. */
  slides: { key: string; label: string; content: ReactNode }[]
}

/**
 * Several charts in one card, one at a time: swipe on a phone, arrows or dots
 * on a laptop. Keeps a page's charts from pushing its list off the screen.
 */
export function ChartCarousel({ slides }: Props) {
  const { t } = useTranslation()
  const trackRef = useRef<HTMLDivElement>(null)
  const [index, setIndex] = useState(0)

  function goTo(i: number) {
    const track = trackRef.current
    const next = Math.max(0, Math.min(slides.length - 1, i))
    setIndex(next)
    track?.scrollTo?.({ left: next * track.clientWidth, behavior: 'smooth' })
  }

  return (
    <Card shadow="sm" className="p-4">
      <div
        ref={trackRef}
        className="flex snap-x snap-mandatory overflow-x-auto [scrollbar-width:none]"
        onScroll={(e) => {
          const el = e.currentTarget
          if (el.clientWidth > 0) setIndex(Math.round(el.scrollLeft / el.clientWidth))
        }}
      >
        {slides.map((slide) => (
          <div
            key={slide.key}
            role="group"
            aria-roledescription="slide"
            aria-label={slide.label}
            className="w-full shrink-0 snap-start"
          >
            {slide.content}
          </div>
        ))}
      </div>
      {slides.length > 1 && (
        <div className="mt-3 flex items-center justify-center gap-2">
          <Button
            isIconOnly
            size="sm"
            variant="light"
            aria-label={t('common.previous')}
            isDisabled={index === 0}
            onPress={() => goTo(index - 1)}
          >
            ‹
          </Button>
          {slides.map((slide, i) => (
            <Button
              key={slide.key}
              isIconOnly
              size="sm"
              variant="light"
              aria-label={slide.label}
              aria-current={i === index}
              className="h-5 w-5 min-w-5"
              onPress={() => goTo(i)}
            >
              <span
                className={`block h-2 w-2 rounded-full ${i === index ? 'bg-primary' : 'bg-default-300'}`}
              />
            </Button>
          ))}
          <Button
            isIconOnly
            size="sm"
            variant="light"
            aria-label={t('common.next')}
            isDisabled={index === slides.length - 1}
            onPress={() => goTo(index + 1)}
          >
            ›
          </Button>
        </div>
      )}
    </Card>
  )
}
